import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Button, Field, Input } from '../components/ui';

export default function Login() {
  const { user, mustChangePassword, refresh } = useSession();
  return mustChangePassword && user ? <ChangePassword name={user.name} /> : <SignIn />;
}

function Frame({ title, sub, children, foot }: {
  title: string; sub: string; children: React.ReactNode; foot?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen grid place-items-center p-7">
      <div className="w-full max-w-[400px] bg-card border border-rule rounded-md shadow-lg p-8">
        <div className="flex items-center gap-2.5 mb-5">
          <span className="grid place-items-center w-7 h-7 rounded-md bg-brand text-brand-ink
                           font-display font-bold text-[17px]">C</span>
          <b className="font-display text-[15px] font-semibold">Cocoma Books</b>
        </div>
        <h1 className="font-display text-[25px] font-bold tracking-[-0.02em] m-0">{title}</h1>
        <p className="mt-2 mb-6 text-[13px] text-ink-2">{sub}</p>
        {children}
        {foot}
      </div>
    </div>
  );
}

function SignIn() {
  const { refresh } = useSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await api.post('/api/auth/login', { username: username.trim(), password });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed.');
      setBusy(false);
    }
  }

  return (
    <Frame title="Sign in" sub="Cocoma Digital Private Limited · internal books"
      foot={
        <p className="mt-5 pt-4 border-t border-rule-soft font-mono text-[11px] leading-relaxed text-muted">
          This runs on the company's own server. If you reach it from anywhere unexpected, say so.
        </p>
      }>
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <Field label="Username">
          <Input value={username} onChange={(e) => setUsername(e.target.value)}
                 autoComplete="username" autoCapitalize="none" placeholder="owner" />
        </Field>
        <Field label="Password">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 autoComplete="current-password" />
        </Field>
        <p className="m-0 min-h-[1em] text-[12.5px] text-critical">{error}</p>
        <Button type="submit" variant="primary" block disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </Frame>
  );
}

function ChangePassword({ name }: { name: string }) {
  const { refresh, signOut } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (next !== again) { setError('Those two do not match.'); return; }
    setBusy(true);
    try {
      await api.post('/api/auth/password', { current, next });
      // The server invalidates every session on a password change, so sign in again.
      await signOut();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
      setBusy(false);
    }
  }

  return (
    <Frame title="Choose your own password"
           sub={`Everyone starts with the same one, ${name.split(' ')[0]} — set yours before going any further.`}>
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <Field label="Current password">
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                 autoComplete="current-password" />
        </Field>
        <Field label="New password">
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                 autoComplete="new-password" placeholder="At least 10 characters" />
        </Field>
        <Field label="Confirm">
          <Input type="password" value={again} onChange={(e) => setAgain(e.target.value)}
                 autoComplete="new-password" />
        </Field>
        <p className="m-0 min-h-[1em] text-[12.5px] text-critical">{error}</p>
        <Button type="submit" variant="primary" block disabled={busy}>
          {busy ? 'Saving…' : 'Set my password'}
        </Button>
      </form>
    </Frame>
  );
}
